import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Search, Sun, Moon, ShoppingBag, Languages, Package, Info, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X } from 'lucide-react';
import CartDrawer from './CartDrawer';

// --- مكون مودال تفاصيل المنتج ---
const ProductDetailsModal = ({ product, isOpen, onClose, addToCart, t, lang, BASE_API_URL }) => {
  const [currentImg, setCurrentImg] = useState(0);
  const images = [product?.image_url, ...(product?.gallery || [])].filter(Boolean);

  if (!product) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
          />
          <motion.div 
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-[#161B22] w-full max-w-2xl rounded-[2.5rem] overflow-hidden shadow-2xl z-10 relative max-h-[90vh] overflow-y-auto"
          >
            <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-white/10 backdrop-blur-md rounded-full text-white sm:text-gray-500">
              <X size={24} />
            </button>

            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 relative bg-gray-100 dark:bg-gray-800 h-64 md:h-auto">
                <img 
                  src={images[currentImg]?.startsWith('http') ? images[currentImg] : `${BASE_API_URL}${images[currentImg]}`}
                  className="w-full h-full object-cover" alt={product.name}
                />
                {images.length > 1 && (
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2">
                    <button onClick={() => setCurrentImg((prev) => (prev > 0 ? prev - 1 : images.length - 1))} className="p-1 bg-white/50 rounded-full"><ChevronRight /></button>
                    <button onClick={() => setCurrentImg((prev) => (prev < images.length - 1 ? prev + 1 : 0))} className="p-1 bg-white/50 rounded-full"><ChevronLeft /></button>
                  </div>
                )}
              </div>
              <div className="md:w-1/2 p-6 sm:p-8 flex flex-col">
                <span className="text-pink-500 font-black text-xs uppercase">{product.category}</span>
                <h2 className="text-2xl sm:text-3xl font-black dark:text-white mt-2 mb-4">{product.name}</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6 flex-grow">{product.description}</p>
                
                <div className="flex justify-between items-center mb-6">
                   <div>
                      <p className="text-pink-600 font-black text-xl">{Number(product.price_syp).toLocaleString()} {t.syp}</p>
                      <p className="text-indigo-500 font-bold text-sm">${product.price}</p>
                   </div>
                   <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-2xl">
                      <span className="text-xs font-bold dark:text-gray-300">{t.stock} {product.stock}</span>
                   </div>
                </div>

                <button 
                  onClick={() => { addToCart(product); onClose(); }}
                  disabled={product.stock <= 0}
                  className="w-full py-4 bg-slate-900 dark:bg-indigo-600 text-white rounded-2xl font-black flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform"
                >
                  <ShoppingCart size={20} /> {product.stock > 0 ? (lang === 'ar' ? 'إضافة للسلة' : 'Add to Cart') : t.out}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// مكون البتلات المتساقطة
const GlobalPetals = ({ isDark }) => {
  const petals = Array.from({ length: 15 });
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {petals.map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -100, x: `${Math.random() * 100}%`, rotate: 0, opacity: 0 }}
          animate={{ y: '110vh', x: `${(Math.random() * 100) - 10}%`, rotate: 720, opacity: [0, 0.4, 0] }}
          transition={{ duration: 15 + Math.random() * 10, repeat: Infinity, ease: "linear", delay: i * 0.5 }}
          className="absolute"
        >
          <div className={`w-2 h-4 blur-[1px] ${isDark ? 'bg-blue-400/20' : 'bg-pink-300/30'}`}
               style={{ borderRadius: '50% 0 50% 50%', transform: `scale(${0.5 + Math.random()})` }} />
        </motion.div>
      ))}
    </div>
  );
};

// --- مكون العروض بحركة التبديل الذكية ---
const OffersGrid = ({ apiBaseUrl, lang }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/sliders`);
        if (response.data) setOffers(response.data);
      } catch (err) { console.error("Offers Error:", err); }
      finally { setLoading(false); }
    };
    fetchOffers();
  }, [apiBaseUrl]);

  useEffect(() => {
    if (offers.length < 2) return;
    const interval = setInterval(() => {
      setOffers(prev => {
        const newArr = [...prev];
        const first = newArr.shift();
        newArr.push(first);
        return newArr;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [offers]);

  if (loading || offers.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 mt-6 sm:mt-10 mb-6">
      <div className="flex items-center gap-3 mb-6 sm:mb-8">
        <div className="w-1.5 h-8 sm:w-2 sm:h-10 bg-gradient-to-t from-pink-600 to-indigo-600 rounded-full" />
        <h2 className="text-xl sm:text-2xl font-black text-gray-800 dark:text-white italic">
          {lang === 'ar' ? 'عروضنا المميزة ✨' : 'Special Offers ✨'}
        </h2>
      </div>

      <div className="flex overflow-x-auto no-scrollbar sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 pb-4">
        <AnimatePresence mode="popLayout">
          {offers.slice(0, 3).map((offer) => (
            <motion.div 
              layout
              key={offer.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="group relative min-w-[85%] sm:min-w-0 h-48 sm:h-64 rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-xl border-4 border-white dark:border-gray-800 flex-shrink-0"
            >
              <img 
                src={offer.image_url || offer.imageUrl} 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-125" 
                alt="offer"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-4 sm:p-8">
                <motion.h3 layout className="text-lg sm:text-xl font-black text-white">{offer.title}</motion.h3>
                <motion.p layout className="text-pink-400 text-xs sm:text-sm font-bold">{offer.sub_title || offer.subTitle}</motion.p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- مكون الوصف القابل للتوسيع ---
const ExpandableDescription = ({ text, lang }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 40; 
  const shouldShowBtn = text && text.length > maxLength;

  return (
    <div className="mb-2">
      <p className="text-[9px] sm:text-[11px] text-gray-500 dark:text-gray-400 leading-tight transition-all">
        {isExpanded ? text : `${text?.substring(0, maxLength)}${shouldShowBtn ? '...' : ''}`}
      </p>
      {shouldShowBtn && (
        <button 
          onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          className="text-pink-500 text-[8px] sm:text-[9px] font-black mt-1 flex items-center gap-1"
        >
          {isExpanded ? (lang === 'ar' ? 'عرض أقل' : 'Show less') : (lang === 'ar' ? 'رؤية المزيد' : 'Read more')}
          {isExpanded ? <ChevronUp size={8} /> : <ChevronDown size={8} />}
        </button>
      )}
    </div>
  );
};

// --- مكون التحكم في صور المنتج (Slider) داخل الكرت ---
const ProductImageSlider = ({ product, BASE_API_URL }) => {
  const [idx, setIdx] = useState(0);
  const images = [product.image_url, ...(product.gallery || [])].filter(Boolean);

  const next = (e) => { e.stopPropagation(); setIdx((prev) => (prev + 1) % images.length); };
  const prev = (e) => { e.stopPropagation(); setIdx((prev) => (prev - 1 + images.length) % images.length); };

  return (
    <div className="relative w-full h-full group/slider overflow-hidden rounded-[1rem] sm:rounded-[2rem]">
      <motion.img 
        key={idx}
        initial={{ opacity: 0.5 }} animate={{ opacity: 1 }}
        src={images[idx]?.startsWith('http') ? images[idx] : `${BASE_API_URL}${images[idx]}`} 
        className="w-full h-full object-cover" 
        alt={product.name} 
      />
      {images.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/30 backdrop-blur-md p-1 rounded-full opacity-0 group-hover/slider:opacity-100 transition-opacity">
            <ChevronLeft size={16} className="text-gray-800" />
          </button>
          <button onClick={next} className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/30 backdrop-blur-md p-1 rounded-full opacity-0 group-hover/slider:opacity-100 transition-opacity">
            <ChevronRight size={16} className="text-gray-800" />
          </button>
          <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1">
            {images.map((_, i) => (
              <div key={i} className={`w-1 h-1 rounded-full ${i === idx ? 'bg-pink-500 w-2' : 'bg-gray-300'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CustomerStore = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [lang, setLang] = useState('ar');
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState('الكل');
  const [selectedProduct, setSelectedProduct] = useState(null); // للمودال

  const BASE_API_URL = "https://my-pharmacy-api.onrender.com";
  const [allProducts, setAllProducts] = useState([]); 

  const fetchProducts = async () => {
    if (allProducts.length > 0 && selectedCategoryId === 'الكل') return; // لا تطلب البيانات إذا كانت موجودة
    
    setLoading(true);
    try {
      // اطلب كل المنتجات مرة واحدة بدون params الفلترة
      const response = await axios.get(`${BASE_API_URL}/api/products`);
      setAllProducts(response.data); // احفظ النسخة الأصلية هنا
      setProducts(response.data);
    } catch (err) { console.error("Fetch Error:", err); }
    finally { setLoading(false); }
  };

// 2. بدلاً من fetch عند كل تغيير، قم بالفلترة محلياً
useEffect(() => {
  if (allProducts.length > 0) {
    const filtered = selectedCategoryId === 'الكل' 
      ? allProducts 
      : allProducts.filter(p => p.category === selectedCategoryId);
    setProducts(filtered);
  } else {
    fetchProducts(); // فقط في أول مرة يفتح فيها الموقع
  }
}, [selectedCategoryId, allProducts]);
  const addToCart = (product) => {
    if (product.stock <= 0) return;
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, stock: p.stock - 1 } : p));
    setIsCartOpen(true);
  };

  const updateQuantity = (id, delta) => {
    setCart(prev => prev.map(item => item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const handleCheckout = async (customerData) => {
    try {
      const formData = new FormData();
      formData.append('customerName', customerData.name);
      formData.append('totalPrice', cartTotal);
      formData.append('details', JSON.stringify(cart)); 
      if (customerData.receipt) formData.append('receipt', customerData.receipt);

      const response = await axios.post(`${BASE_API_URL}/api/orders`, formData);
      if (response.status === 200 || response.status === 201) {
        await fetchProducts(); 
        setCart([]);
        setIsCartOpen(false);
        alert(lang === 'ar' ? "✅ تم تسجيل طلبك بنجاح!" : "✅ Order placed successfully!");
      }
    } catch (err) {
      alert(lang === 'ar' ? "❌ فشل إتمام الطلب" : "❌ Checkout failed");
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (Number(item.price_syp) * item.quantity), 0);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [isDarkMode]);

  const categories = [
    { id: 'الكل', label: { ar: 'الكل ✨', en: 'All' } },
    { id: 'baby-kids', label: { ar: ' بيبي وأطفال', en: 'baby-kids' } },
    { id: 'Supplements', label: { ar: 'مكملات', en: 'Supplements' } },
    { id: 'Cosmetics-Beauty', label: { ar: 'مستحضرات تجميل', en: 'Cosmetics-Beauty' } },
  ];

  const t = {
    ar: { search: 'ابحث عن دوائك هنا...', syp: 'ل.س', usd: '$', stock: 'المتوفر:', out: 'نفذت', desc: 'حول المنتج' },
    en: { search: 'Search meds...', syp: 'SYP', usd: '$', stock: 'Stock:', out: 'Out', desc: 'Description' }
  }[lang];

  return (
    <div className="min-h-screen bg-[#FDF8FA] dark:bg-[#0B0E14] transition-colors duration-500 font-sans pb-10 flex flex-col overflow-x-hidden" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <GlobalPetals isDark={isDarkMode} />

      {/* مودال التفاصيل */}
      <ProductDetailsModal 
        product={selectedProduct} 
        isOpen={!!selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
        addToCart={addToCart} t={t} lang={lang} BASE_API_URL={BASE_API_URL}
      />

      <CartDrawer 
        isOpen={isCartOpen} onClose={() => setIsCartOpen(false)}
        cart={cart} updateQuantity={updateQuantity}
        removeFromCart={removeFromCart} cartTotal={cartTotal} lang={lang}
        onCheckout={handleCheckout} 
      />

      <header className="relative z-20 px-2 sm:px-4 pt-4 sm:pt-8 max-w-6xl mx-auto w-full">
        <div className="bg-white/80 dark:bg-[#161B22]/80 backdrop-blur-md rounded-[2rem] sm:rounded-[3rem] p-4 sm:p-8 shadow-xl border border-white dark:border-gray-800">
          <div className="flex flex-row justify-between items-center mb-6 sm:mb-10 gap-2">
            <div className="flex gap-1.5 sm:gap-3">
              <button onClick={() => setIsDarkMode(!isDarkMode)} className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-pink-50 dark:bg-gray-800 flex items-center justify-center text-pink-500 shadow-sm">
                {isDarkMode ? <Sun size={16}/> : <Moon size={16}/>}
              </button>
              <button onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-indigo-50 dark:bg-gray-800 flex items-center justify-center text-indigo-600 font-bold text-xs sm:text-base">
                <Languages size={16} />
              </button>
            </div>

            <div className="flex flex-col items-center flex-1">
                <motion.div 
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative group cursor-pointer"
                >
                  <div className="bg-gradient-to-br from-gray-900 to-black dark:from-indigo-600 dark:to-purple-800 px-4 py-2 sm:px-8 sm:py-4 rounded-2xl sm:rounded-[2rem] shadow-2xl flex items-center border-b-2 sm:border-b-4 border-pink-500/50">
                      <div className="relative">
                        <h1 className="text-xl sm:text-4xl font-black text-white italic tracking-tighter" style={{ fontFamily: "'Playfair Display', serif" }}>
                          ISoon
                        </h1>
                        <span className="absolute -top-1 -right-3 sm:-right-4 text-pink-400 font-bold text-[8px] sm:text-xs">®</span>
                      </div>
                      <h1 className="text-xs sm:text-2xl font-light text-white/60 tracking-widest ml-2 uppercase">Pharma</h1>
                  </div>
                </motion.div>    
            </div>

            <button onClick={() => setIsCartOpen(true)} className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-slate-900 dark:bg-indigo-600 flex items-center justify-center text-white relative shadow-xl">
              <ShoppingBag size={18} />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-[8px] sm:text-[9px] w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center border-2 border-white font-black">
                  {cart.reduce((a, b) => a + b.quantity, 0)}
                </span>
              )}
            </button>
          </div>

          <div className="max-w-xl mx-auto relative px-1">
            <input 
              type="text" 
              placeholder={t.search} 
              className="w-full py-3 sm:py-4 px-10 sm:px-14 rounded-full bg-[#F5F7FB] dark:bg-[#0D1117] text-center text-xs sm:text-lg shadow-inner focus:ring-2 focus:ring-pink-400 border-none dark:text-white" 
              onChange={(e) => setSearchTerm(e.target.value)} 
            />
            <Search className={`absolute ${lang === 'ar' ? 'right-5' : 'left-5'} top-1/2 -translate-y-1/2 text-pink-400`} size={16} />
          </div>
        </div>
      </header>

      <OffersGrid apiBaseUrl={BASE_API_URL} lang={lang} />

      <nav className="max-w-6xl mx-auto w-full px-4 mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-2 z-10 sticky top-2">
          {categories.map((cat) => (
            <button 
              key={cat.id} 
              onClick={() => setSelectedCategoryId(cat.id)} 
              className={`px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl text-[10px] sm:text-xs font-black transition-all whitespace-nowrap shadow-sm ${selectedCategoryId === cat.id ? 'bg-[#FF4D80] text-white scale-105' : 'bg-white dark:bg-[#161B22] text-gray-500'}`}
            >
              {cat.label[lang]}
            </button>
          ))}
      </nav>

      <main className="max-w-6xl mx-auto px-2 sm:px-4 py-6 sm:py-8 flex-grow">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-10">
            <AnimatePresence mode='popLayout'>
              {products
                .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((product) => (
                  <motion.div 
                    layout
                    key={product.id} 
                    initial={{ opacity: 0, scale: 0.9 }} 
                    animate={{ opacity: 1, scale: 1 }} 
                    onClick={() => setSelectedProduct(product)}
                    className="group bg-white dark:bg-[#161B22] rounded-[1.5rem] sm:rounded-[2.5rem] p-2 sm:p-4 shadow-md border border-gray-50 dark:border-gray-800 flex flex-col relative overflow-hidden cursor-pointer"
                  >
                    <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 bg-white/90 dark:bg-gray-800 px-1.5 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                      <Package size={8} className="text-pink-500" />
                      <span className="text-[7px] sm:text-[10px] font-black text-gray-600 dark:text-gray-300">{t.stock} {product.stock}</span>
                    </div>

                    <div className="aspect-square bg-[#FDF8FA] dark:bg-[#0D1117] rounded-[1rem] sm:rounded-[2rem] mb-2 sm:mb-4 overflow-hidden relative">
                      {/* عرض الصور مع الأسهم */}
                      <ProductImageSlider product={product} BASE_API_URL={BASE_API_URL} />
                    </div>

                    <div className="flex-1 px-1">
                      <span className="text-[7px] sm:text-[9px] font-black text-indigo-500 uppercase">{product.category}</span>
                      <h3 className="text-xs sm:text-lg font-black dark:text-white mt-0.5 mb-1 truncate">{product.name}</h3>
                      
                      <ExpandableDescription text={product.description} lang={lang} />
                      
                      <div className="flex flex-col sm:flex-row items-center justify-between mb-2 sm:mb-4 p-1.5 sm:p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl gap-1">
                          <div className="text-center sm:text-right w-full sm:w-auto">
                            <p className="text-[6px] sm:text-[8px] font-bold text-gray-400 leading-none">{t.syp}</p>
                            <p className="text-[10px] sm:text-md font-black text-pink-600">{Number(product.price_syp).toLocaleString()}</p>
                          </div>
                          <div className="hidden sm:block h-4 w-[1px] bg-gray-200"></div>
                          <div className="text-center sm:text-left w-full sm:w-auto">
                            <p className="text-[6px] sm:text-[8px] font-bold text-gray-400 leading-none">{t.usd}</p>
                            <p className="text-[10px] sm:text-md font-black text-indigo-600">${Number(product.price).toFixed(2)}</p>
                          </div>
                      </div>

                      <button 
                        onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                        disabled={product.stock <= 0}
                        className={`w-full py-2 sm:py-4 rounded-lg sm:rounded-xl font-black text-[9px] sm:text-sm flex items-center justify-center gap-1.5 transition-all active:scale-95 ${product.stock > 0 ? 'bg-slate-900 dark:bg-indigo-600 text-white shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                      >
                        <ShoppingCart size={12} /> {product.stock > 0 ? (lang === 'ar' ? 'إضافة' : 'Add') : t.out}
                      </button>
                    </div>
                  </motion.div>
                ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      <footer className="w-full py-6 sm:py-10 mt-auto text-center z-10">
          <p className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-gray-600 tracking-widest uppercase">
            {t.dev}
          </p>
      </footer>
    </div>
  );
};

export default CustomerStore;